import {useMemo} from 'react';
import {kr, tickLabels} from '../../lib/format.js';
import {Defs, fillById, niceScale, Grid, useHover, HitBands, Tooltip, Legend} from './primitives.jsx';

const W = 560, H = 300, L = 60, R = 10, T = 10, B = 24;

/**
 * What the cash curve is made of: expected receipts up, expected payments down, and the
 * net as a marker on each month.
 *
 * The stack separates money ALREADY OWED to us from money we have yet to earn. The first
 * two months are dominated by the receivable unwind — 409 kkr that is already invoiced —
 * and after that the projection is nothing but run-rate. Reading the cash line without
 * that split makes the opening months look like trading performance when they are a
 * one-time collection of work already done.
 */
const IN = [
	{key: 'receivable_in', label: 'Befintliga kundfordringar', fill: 'solid'},
	{key: 'sales_in', label: 'Ny försäljning (run-rate)', fill: 'open'},
];
const OUT = [
	{key: 'payable_out', label: 'Befintliga leverantörsskulder', fill: 'cross'},
	{key: 'operating_out', label: 'Rörelsekostnad (run-rate)', fill: 'hatch'},
];

export default function ForwardFlows({rows}) {
	const {at, enter, leave} = useHover();
	const ticks = useMemo(() => tickLabels(rows), [rows]);
	const g = useMemo(() => {
		const hi = Math.max(0, ...rows.map(r => IN.reduce((a, s) => a + (r[s.key] ?? 0), 0)));
		const lo = -Math.max(0, ...rows.map(r => OUT.reduce((a, s) => a + (r[s.key] ?? 0), 0)));
		const sc = niceScale(lo, hi);
		const iw = W - L - R, ih = H - T - B;
		return {
			sc,
			y: v => T + ih - ((v - sc.lo) / (sc.hi - sc.lo)) * ih,
			x: i => L + (iw * (i + 0.5)) / rows.length,
			bw: Math.max(3, Math.min(26, (iw / rows.length) * 0.66)),
		};
	}, [rows]);

	if (!rows.length) return null;

	const stack = (r, i, series, sign) => {
		let run = 0;
		return series.map(s => {
			const v = r[s.key] ?? 0;
			if (!v) return null;
			const from = g.y(sign * run), to = g.y(sign * (run + v));
			run += v;
			const h = Math.abs(to - from);
			if (h < 0.4) return null;
			return <rect key={`${r.month}-${s.key}`} x={g.x(i) - g.bw / 2} y={Math.min(from, to)}
				width={g.bw} height={Math.max(h, 0.6)} rx={g.bw > 6 ? 2 : 0} {...fillById(s.fill)} />;
		});
	};

	return (
		<div className="chart">
			<div className="chart__head">
				<span className="chart__title">Förväntade in- och utbetalningar</span>
				<span className="chart__hint">
					per framskriven månad. Prickarna är nettot — det som flyttar kassalinjen.
				</span>
			</div>
			<div className="chart__canvas">
				<svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid meet" role="img"
					aria-label="Förväntade in- och utbetalningar per framskriven månad">
					<Defs />
					<Grid {...g.sc} y={g.y} L={L} R={R} W={W} />
					{rows.map((r, i) => stack(r, i, IN, 1))}
					{rows.map((r, i) => stack(r, i, OUT, -1))}
					<line x1={L} x2={W - R} y1={g.y(0)} y2={g.y(0)} stroke="#000" strokeWidth={1} />
					<polyline fill="none" stroke="#000" strokeWidth={1.4} strokeDasharray="2 3"
						points={rows.map((r, i) => `${g.x(i)},${g.y(r.net_cash ?? 0)}`).join(' ')} />
					{rows.map((r, i) => (
						<circle key={`n-${r.month}`} cx={g.x(i)} cy={g.y(r.net_cash ?? 0)} r={2.6}
							fill="#fff" stroke="#000" strokeWidth={1.4} />
					))}
					{ticks.map((t, i) => (t == null ? null : (
						<text key={rows[i].month} x={g.x(i)} y={H - 8} className="axs" textAnchor="middle">{t}</text>
					)))}
					<HitBands months={rows} x={g.x} W={W} H={H} onEnter={enter} onLeave={leave} />
				</svg>
				<Tooltip months={rows} at={at} x={g.x} W={W} title={m => `${m.label ?? m.month} · prognos`}
					rows={m => [
						...IN.map(s => [s.label, `${kr(m[s.key])} kr`]),
						...OUT.map(s => [s.label, `−${kr(m[s.key])} kr`]),
						['Netto', `${kr(m.net_cash)} kr`],
						['Utgående kassa', `${kr(m.cash)} kr`],
					]} />
			</div>
			<Legend items={[...IN, ...OUT].map(s => ({label: s.label, fill: s.fill}))} />
		</div>
	);
}
